import { fastapi } from './api';

/** A single SSE connection; SSEManager owns retry timing and fresh credentials. */
export class AuthenticatedEventStream {
  static readonly OPEN = 1;
  readyState = 0;
  onopen: (() => void) | null = null;
  onerror: ((error: unknown) => void) | null = null;
  private controller = new AbortController();
  private listeners = new Map<string, Array<(event: MessageEvent) => void>>();

  constructor(url: string) {
    void this.read(url);
  }

  addEventListener(type: string, listener: (event: MessageEvent) => void): void {
    this.listeners.set(type, [...(this.listeners.get(type) || []), listener]);
  }

  close(): void {
    this.readyState = 2;
    this.controller.abort();
  }

  private async read(url: string): Promise<void> {
    try {
      const response = await fastapi.get<ReadableStream<Uint8Array>>(url, {
        adapter: 'fetch',
        responseType: 'stream',
        headers: { Accept: 'text/event-stream' },
        signal: this.controller.signal,
      });
      if (this.controller.signal.aborted) return;
      if (!String(response.headers['content-type']).includes('text/event-stream')) {
        await response.data?.cancel();
        throw new Error('Expected a chat event stream');
      }
      const reader = response.data.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let eventType = '';
      let data: string[] = [];
      this.readyState = AuthenticatedEventStream.OPEN;
      this.onopen?.();
      try {
        while (!this.controller.signal.aborted) {
          const chunk = await reader.read();
          if (chunk.done) throw new Error('Chat event stream ended');
          buffer += decoder.decode(chunk.value, { stream: true });
          let boundary: number;
          while ((boundary = buffer.search(/[\r\n]/)) !== -1) {
            // Keep a trailing CR until the next chunk to recognize split CRLF.
            if (buffer[boundary] === '\r' && boundary === buffer.length - 1) break;
            const line = buffer.slice(0, boundary);
            const length = buffer.slice(boundary, boundary + 2) === '\r\n' ? 2 : 1;
            buffer = buffer.slice(boundary + length);
            if (line === '') {
              if (data.length) {
                const event = new MessageEvent(eventType || 'message', { data: data.join('\n') });
                for (const listener of this.listeners.get(event.type) || []) {
                  listener(event);
                  if (this.controller.signal.aborted) return;
                }
              }
              eventType = '';
              data = [];
            } else {
              const colon = line.indexOf(':');
              const field = colon < 0 ? line : line.slice(0, colon);
              const value = colon < 0 ? '' : line.slice(colon + 1).replace(/^ /, '');
              if (field === 'event') eventType = value;
              if (field === 'data') data.push(value);
            }
          }
        }
      } finally {
        await reader.cancel().catch(() => {});
        reader.releaseLock();
      }
    } catch (error) {
      if (!this.controller.signal.aborted) {
        this.readyState = 2;
        this.onerror?.(error);
      }
    }
  }
}
